import { useCallback, useRef, useState } from 'react'
import {
  summarize,
  type ClassifiedRow,
  type RowClassification,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, EmptyState, ErrorNote, Money } from '@/components/ui'
import { formatDate } from '@/lib/dates'
import { useCsiuImport } from './hooks'

/**
 * CSIU import — REQUIREMENTS.md §6.4, workflow §7.3.
 *
 * Drop every code's export at once. Files are routed by the `AccountCode`
 * column, so ordering and filenames don't matter — which is the point, since
 * CSIU has no bulk export and Matt ends up with ten separate downloads.
 *
 * **Most of an import is discard.** The reference export is 89 P-Card rows out
 * of 108, all skipped because those charges are already captured at purchase.
 * The summary leads with that so a screen reporting "13 to import" from a
 * 108-row file reads as correct rather than broken.
 */

const LABELS: Record<RowClassification, string> = {
  new: 'New',
  duplicate_exact: 'Already recorded',
  duplicate_potential: 'Possible match',
  reversal_pair: 'Reversal pair',
  skip_pcard: 'P-Card — skipped',
}

const TONES: Record<RowClassification, string> = {
  new: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  duplicate_exact: 'bg-slate-50 text-slate-700 border-slate-200',
  duplicate_potential: 'bg-amber-50 text-amber-900 border-amber-200',
  reversal_pair: 'bg-sky-50 text-sky-900 border-sky-200',
  skip_pcard: 'bg-slate-50 text-slate-500 border-slate-200',
}

export function CsiuImportScreen() {
  const {
    files,
    classified,
    excluded,
    unknownAccountCodes,
    load,
    toggle,
    reset,
    commit,
  } = useCsiuImport()

  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [reading, setReading] = useState(false)
  const [result, setResult] = useState<string>()

  const handleFiles = useCallback(
    async (list: FileList | null) => {
      if (list === null || list.length === 0) return
      setReading(true)
      setResult(undefined)
      try {
        await load([...list])
      } finally {
        setReading(false)
      }
    },
    [load],
  )

  const summary = summarize(classified)
  const failed = files.filter((f) => f.error !== undefined)
  const unreadableRows = files.flatMap((f) =>
    f.problems.map((problem) => ({ ...problem, filename: f.filename })),
  )
  const importable = classified.filter(
    (c) => c.classification !== 'skip_pcard' && !excluded.has(c.row.rowNumber),
  )

  // Split, because "Import 19 rows" is a lie when all nineteen already exist
  // and the commit would only mark them confirmed. Re-importing an export is
  // routine, so this is the common case rather than an edge one.
  const willCreate = importable.filter(
    (c) => c.classification === 'new' || c.classification === 'reversal_pair',
  ).length
  const willConfirm = importable.length - willCreate

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Import from CSIU</h1>
        <p className="text-sm text-slate-500">
          Drop every code's export at once — they're sorted by account code, so
          the order and the filenames don't matter.
        </p>
      </div>

      {classified.length === 0 ? (
        <div
          onDragEnter={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setDragging(false)
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            void handleFiles(e.dataTransfer.files)
          }}
          className={`rounded-xl border-2 border-dashed px-6 py-12 text-center transition ${
            dragging
              ? 'border-slate-900 bg-slate-100'
              : 'border-slate-300 bg-white hover:border-slate-400'
          }`}
        >
          <p className="font-medium text-slate-900">
            {reading ? 'Reading…' : 'Drop CSIU exports here'}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            One file per budget code, .xlsx or .csv. Several at once is the point.
          </p>
          <Button
            variant="primary"
            className="mt-4"
            onClick={() => inputRef.current?.click()}
            disabled={reading}
          >
            Choose files
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="sr-only"
            onChange={(e) => {
              void handleFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      ) : (
        <>
          <Card className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium text-slate-900">
                  {files.length} file{files.length === 1 ? '' : 's'} ·{' '}
                  {summary.total} rows
                </p>
                <p className="text-sm text-slate-600">
                  {files.map((f) => f.filename).join(', ')}
                </p>
              </div>
              <Button onClick={reset} disabled={commit.isPending}>
                Start over
              </Button>
            </div>

            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Stat label="To import" value={summary.fresh} tone="text-emerald-700" />
              <Stat
                label="Needs a look"
                value={summary.potentialDuplicates}
                tone="text-amber-700"
              />
              <Stat label="Reversals" value={summary.reversalPairs} tone="text-sky-700" />
              <Stat
                label="Already recorded"
                value={summary.exactDuplicates}
                tone="text-slate-600"
              />
              <Stat label="P-Card skipped" value={summary.skipped} tone="text-slate-500" />
            </dl>

            {/* Said plainly, because a 108-row file reporting 13 imports looks
                like a failure until you know why. */}
            {summary.skipped > 0 && (
              <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
                {summary.skipped} P-Card {summary.skipped === 1 ? 'row is' : 'rows are'}{' '}
                skipped on purpose — those charges were already captured when you
                photographed the receipt. Importing them would count them twice.
              </p>
            )}
          </Card>

          {failed.length > 0 && (
            <ErrorNote>
              {failed.map((f) => (
                <span key={f.filename} className="block">
                  {f.filename}: {f.error}
                </span>
              ))}
            </ErrorNote>
          )}

          {/* Said out loud because the alternative was silence: a row whose
              amount could not be read used to disappear, and the counts above
              then added up perfectly against a file with one more line in
              it. */}
          {unreadableRows.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">
                {unreadableRows.length}{' '}
                {unreadableRows.length === 1 ? 'row was' : 'rows were'} skipped because
                the amount could not be read
              </p>
              <ul className="mt-1 space-y-0.5">
                {unreadableRows.map((problem) => (
                  <li key={`${problem.filename}-${problem.rowNumber}`}>
                    Row {problem.rowNumber} ({problem.row}) — {problem.reason}
                  </li>
                ))}
              </ul>
              <p className="mt-2">
                These are not counted above. Check them in the export and add them by
                hand if they belong in the ledger.
              </p>
            </div>
          )}

          {unknownAccountCodes.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">
                No budget code set up for {unknownAccountCodes.length} account code
                {unknownAccountCodes.length === 1 ? '' : 's'}
              </p>
              <ul className="mt-1 font-mono text-xs">
                {unknownAccountCodes.map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </ul>
              <p className="mt-2">
                Those rows will be skipped. Add the code on the Budget codes
                screen and import again — filing a charge against the wrong code
                is worse than not filing it.
              </p>
            </div>
          )}

          <RowTable rows={classified} excluded={excluded} onToggle={toggle} />

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              onClick={() =>
                commit.mutate(undefined, {
                  onSuccess: (r) =>
                    setResult(
                      `Imported ${r.created} new, confirmed ${r.confirmed} already recorded, skipped ${r.skipped}.`,
                    ),
                })
              }
              disabled={commit.isPending || importable.length === 0}
            >
              {commit.isPending ? 'Importing…' : commitLabel(willCreate, willConfirm)}
            </Button>
            {commit.error !== null && (
              <span role="alert" className="text-sm text-red-700">
                {commit.error.message}
              </span>
            )}
          </div>
        </>
      )}

      {result !== undefined && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {result}
        </p>
      )}

      {classified.length === 0 && result === undefined && files.length === 0 && (
        <EmptyState title="Nothing imported yet">
          <p>
            Export each budget code from CSIU, then drop all the files here
            together.
          </p>
        </EmptyState>
      )}
    </div>
  )
}

/** Names both halves of what the commit does, so neither is a surprise. */
function commitLabel(create: number, confirm: number): string {
  const rows = (n: number) => `${n} row${n === 1 ? '' : 's'}`
  if (create > 0 && confirm > 0) return `Import ${create}, confirm ${confirm}`
  if (create > 0) return `Import ${rows(create)}`
  if (confirm > 0) return `Confirm ${rows(confirm)} already recorded`
  return 'Nothing to import'
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`text-2xl font-semibold tabular ${tone}`}>{value}</dd>
    </div>
  )
}

function RowTable({
  rows,
  excluded,
  onToggle,
}: {
  rows: readonly ClassifiedRow[]
  excluded: ReadonlySet<number>
  onToggle: (rowNumber: number) => void
}) {
  // Skipped rows are collapsed by default. They are the majority of the file
  // and the least interesting part of it, but hiding them outright would make
  // the counts impossible to check.
  const [showSkipped, setShowSkipped] = useState(false)
  const visible = showSkipped
    ? rows
    : rows.filter((r) => r.classification !== 'skip_pcard')
  const skippedCount = rows.length - rows.filter((r) => r.classification !== 'skip_pcard').length

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Rows
        </h2>
        {skippedCount > 0 && (
          <Button variant="ghost" onClick={() => setShowSkipped((s) => !s)}>
            {showSkipped ? 'Hide' : 'Show'} {skippedCount} skipped
          </Button>
        )}
      </div>

      <ul className="space-y-2">
        {visible.map((entry) => {
          const isSkipped = entry.classification === 'skip_pcard'
          const isExcluded = excluded.has(entry.row.rowNumber)
          return (
            <li
              key={entry.row.rowNumber}
              className={`rounded-lg border px-4 py-3 ${TONES[entry.classification]} ${
                isExcluded ? 'opacity-50' : ''
              }`}
            >
              <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                {!isSkipped && (
                  <input
                    type="checkbox"
                    checked={!isExcluded}
                    onChange={() => onToggle(entry.row.rowNumber)}
                    className="mt-1 size-4 rounded border-slate-300"
                    aria-label={`Include ${entry.row.vendor ?? entry.row.description}`}
                  />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {entry.row.vendor ?? (entry.row.description || '(no vendor)')}
                    </span>
                    <Badge>{LABELS[entry.classification]}</Badge>
                  </div>
                  <p className="mt-0.5 text-xs opacity-80">
                    {formatDate(entry.row.transactionDate)} · {entry.row.description}
                  </p>
                  <p className="mt-0.5 text-xs opacity-70">{entry.reason}</p>
                </div>

                <Money value={entry.row.amount} className="font-medium" />
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
